////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

/**
 * ════════════════════════════════════════════════════════════════════════════════
 * PAYMENT PEA - SUBSCRIPTION PRESENTATION (POD/PEA PATTERN) - FINAL COMPONENT!
 * ════════════════════════════════════════════════════════════════════════════════
 *
 * ARCHITECTURAL ROLE: Pure UI component rendering pricing tiers, feature comparison,
 * billing toggle, and subscription selection interface
 *
 * I[R{DVT}]O PATTERN:
 *   INPUT: Pricing tiers array, selected tier, billing cycle, loading state from Pod
 *   REQUIREMENTS: Interactive pricing cards, monthly/annual toggle, feature lists, CTA buttons
 *   OUTPUT: Beautiful pricing UI with tier selection, Stripe checkout initiation
 *
 * FEATURES: Responsive pricing grid, "Most Popular" badge, feature comparison, FAQ section
 * CONVERSION OPTIMIZATION: Clear value props, social proof, money-back guarantee messaging
 */

import React from 'react';
import styles from './Bitsbound_Kings_Customer_Auth_SRC_Payment_Lobby_Pea.module.css';

interface PricingTier {
  name: string;
  displayName: string;
  price: { monthly: number; annual: number };
  features: string[];
  limits: {
    apiCalls: number;
    arbiters: string[];
    support: string;
  };
  recommended?: boolean;
}

interface PaymentPeaProps {
  pricingTiers: PricingTier[];
  selectedTier: string;
  billingCycle: 'monthly' | 'annual';
  isLoading: boolean;
  onSelectTier: (tierName: string) => void;
  onToggleBilling: () => void;
  onSubscribe: () => void;
  onContactSales: () => void;
}

const Pea_Lobby_Payment_SRC_Auth_Customer_Kings: React.FC<PaymentPeaProps> = ({
  pricingTiers,
  selectedTier,
  billingCycle,
  isLoading,
  onSelectTier,
  onToggleBilling,
  onSubscribe,
  onContactSales
}) => {
  const formatPrice = (price: number): string => {
    if (price === 0) return 'Free';
    return `$${price.toLocaleString()}`;
  };

  const formatApiCalls = (calls: number): string => {
    if (calls === -1) return 'Unlimited';
    if (calls >= 1000000) return `${calls / 1000000}M`;
    if (calls >= 1000) return `${calls / 1000}K`;
    return calls.toString();
  };

  return (
    <div className={styles.paymentContainer}>
      <div className={styles.paymentHeader}>
        <h1 className={styles.title}>Choose Your Plan</h1>
        <p className={styles.subtitle}>
          Start with 14 days free. Upgrade, downgrade, or cancel anytime.
        </p>
        
        <div className={styles.billingToggle}>
          <button
            className={`${styles.billingOption} ${billingCycle === 'monthly' ? styles.active : ''}`}
            onClick={billingCycle === 'annual' ? onToggleBilling : undefined}
          >
            Monthly
          </button>
          <button
            className={`${styles.billingOption} ${billingCycle === 'annual' ? styles.active : ''}`}
            onClick={billingCycle === 'monthly' ? onToggleBilling : undefined}
          >
            Annual
            <span className={styles.saveBadge}>Save 20%</span>
          </button>
        </div>
      </div>

      <div className={styles.pricingGrid}>
        {pricingTiers.map((tier) => (
          <div
            key={tier.name}
            className={`${styles.pricingCard} ${
              selectedTier === tier.name ? styles.selected : ''
            } ${tier.recommended ? styles.recommended : ''}`}
            onClick={() => onSelectTier(tier.name)}
          >
            {tier.recommended && (
              <div className={styles.recommendedBadge}>Most Popular</div>
            )}
            
            <div className={styles.tierHeader}>
              <h2 className={styles.tierName}>{tier.displayName}</h2>
              <div className={styles.priceWrapper}>
                <span className={styles.price}>
                  {formatPrice(tier.price[billingCycle])}
                </span>
                {tier.price[billingCycle] > 0 && (
                  <span className={styles.period}>
                    /{billingCycle === 'monthly' ? 'month' : 'year'}
                  </span>
                )}
              </div>
            </div>

            <div className={styles.tierLimits}>
              <div className={styles.limit}>
                <span className={styles.limitLabel}>API Calls</span>
                <span className={styles.limitValue}>
                  {formatApiCalls(tier.limits.apiCalls)}/month
                </span>
              </div>
              <div className={styles.limit}>
                <span className={styles.limitLabel}>Arbiters</span>
                <span className={styles.limitValue}>
                  {tier.limits.arbiters.join(', ')}
                </span>
              </div>
              <div className={styles.limit}>
                <span className={styles.limitLabel}>Support</span>
                <span className={styles.limitValue}>{tier.limits.support}</span>
              </div>
            </div>

            <ul className={styles.featuresList}>
              {tier.features.map((feature, index) => (
                <li key={index} className={styles.featureItem}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={styles.featureIcon}>
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>

            {/* T_TIER_RENAME_2026_01_02: tier_four is enterprise-level with Contact Sales CTA */}
            {tier.name === 'tier_four' ? (
              <button
                className={`${styles.selectButton} ${styles.contactButton}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onContactSales();
                }}
              >
                Contact Sales
              </button>
            ) : (
              <button
                className={`${styles.selectButton} ${
                  selectedTier === tier.name ? styles.selected : ''
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectTier(tier.name);
                }}
              >
                {selectedTier === tier.name ? 'Selected' : 'Select Plan'}
              </button>
            )}
          </div>
        ))}
      </div>

      {/* T_TIER_RENAME_2026_01_02: tier_four requires Contact Sales, not checkout */}
      {selectedTier && selectedTier !== 'tier_four' && (
        <div className={styles.checkoutSection}>
          <button
            className={styles.subscribeButton}
            onClick={onSubscribe}
            disabled={isLoading}
          >
            {isLoading ? 'Processing...' : 'Continue to Payment'}
          </button>
          <p className={styles.secureText}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0110 0v4"/>
            </svg>
            Secure payment powered by Stripe
          </p>
        </div>
      )}

      <div className={styles.features}>
        <h3 className={styles.featuresTitle}>All Plans Include</h3>
        <div className={styles.featuresGrid}>
          <div className={styles.featureCard}>
            <div className={styles.featureIconWrapper}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
              </svg>
            </div>
            <h4>Sub-5 Second Decisions</h4>
            <p>Lightning-fast compliance decisions in production</p>
          </div>
          <div className={styles.featureCard}>
            <div className={styles.featureIconWrapper}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <polyline points="9 12 12 15 16 10"/>
              </svg>
            </div>
            <h4>Cryptographic Proof</h4>
            <p>Every decision includes audit trail and proof bundle</p>
          </div>
          <div className={styles.featureCard}>
            <div className={styles.featureIconWrapper}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
            </div>
            <h4>Global Compliance</h4>
            <p>GDPR, CCPA, HIPAA, and more regulations supported</p>
          </div>
          <div className={styles.featureCard}>
            <div className={styles.featureIconWrapper}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
              </svg>
            </div>
            <h4>Easy Integration</h4>
            <p>SDKs for all major languages and frameworks</p>
          </div>
        </div>
      </div>

      <div className={styles.faq}>
        <h3 className={styles.faqTitle}>Frequently Asked Questions</h3>
        <div className={styles.faqItem}>
          <h4>Can I change plans anytime?</h4>
          <p>Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.</p>
        </div>
        <div className={styles.faqItem}>
          <h4>What happens after the trial?</h4>
          <p>After 14 days, you'll need to select a paid plan to continue. No credit card required for trial.</p>
        </div>
        <div className={styles.faqItem}>
          <h4>Do you offer custom pricing?</h4>
          {/* T_TIER_RENAME_2026_01_02: Updated Enterprise to Tier Four */}
          <p>Yes! Our Tier Four plan offers custom pricing based on your needs. Contact sales for details.</p>
        </div>
      </div>
    </div>
  );
};

export default Pea_Lobby_Payment_SRC_Auth_Customer_Kings;